import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { OverlayLoaderComponent } from '../../../common/components/overlay-loader/overlay-loader.component';
import { ToasterService } from '../../../../ui-components/services/toast/toast.service';
import { PlanUsageService } from './plan-usage.service';
import { atLimit, PlanLimitView, PlanSubscription, usageRatio } from './plan-usage.model';
import { SubscriptionStatusEnum } from './subscription-status.enum';

const STATUS_BADGE_CLASSES: Record<string, string> = {
  [SubscriptionStatusEnum.ACTIVE]: 'bg-success-500',
  [SubscriptionStatusEnum.PENDING_PAYMENT]: 'bg-warning-500',
  [SubscriptionStatusEnum.SCHEDULED]: 'bg-primary-500',
  [SubscriptionStatusEnum.CANCELLED]: 'bg-destructive-500',
  [SubscriptionStatusEnum.EXPIRED]: 'bg-neutral-900',
};

@Component({
  selector: 'app-plan-usage',
  imports: [TranslocoPipe, OverlayLoaderComponent, DecimalPipe, DatePipe],
  providers: [provideSystemTabsLayout()],
  templateUrl: './plan-usage.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanUsageComponent implements OnInit {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly service = inject(PlanUsageService);
  private readonly toaster = inject(ToasterService);

  readonly subscription = signal<PlanSubscription | null>(null);
  readonly isLoading = signal(true);
  /** Stays false after a failed load, so the page never claims "no active plan" on an error. */
  readonly isLoaded = signal(false);
  readonly subscriptionStatusEnum = SubscriptionStatusEnum;
  /** Floating layout: solid status badge, the same colours as status badges elsewhere in the system. */
  readonly statusBadgeClass = computed(() => {
    const status = this.subscription()?.status.value;
    return (status && STATUS_BADGE_CLASSES[status]) || 'bg-neutral-500';
  });

  /** Only an active subscription carries limits; each row's bar fill and at-limit flag are derived once, not per check. */
  readonly limitRows = computed<PlanLimitView[] | null>(
    () => this.subscription()?.limits?.map(row => ({ ...row, ratio: usageRatio(row), atLimit: atLimit(row) })) ?? null,
  );

  ngOnInit() {
    this.loadPlanUsage();
  }

  private loadPlanUsage(): void {
    this.service.get().subscribe({
      next: res => {
        this.subscription.set(res.data ?? null);
        this.isLoaded.set(true);
        this.isLoading.set(false);
      },
      // The error interceptor rethrows the response body: `error.message` is the backend text.
      error: error => {
        this.isLoading.set(false);
        this.toaster.add(error?.message, '', '', 'error', true, true);
      },
    });
  }
}
